import React, { forwardRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import { AppMenuProps, AppSubmenuProps, SideBarItem } from './AppMenuTypes';
import { getInk, removeClass } from '@/src/utils/htmlUtils';
import AppMenuItem from './AppMenuItem';

const AppSubmenu = forwardRef<HTMLUListElement, AppSubmenuProps>(
  (props, ref) => {
    const { pathname } = useLocation();
    const getInitialActiveIndex = () => {
      return (
        props.items?.findIndex(
          (item) => item.to && pathname.startsWith(item.to)
        ) ?? null
      );
    };
    const [activeIndex, setActiveIndex] = useState<number | null>(
      getInitialActiveIndex
    );
    const isHorizontal = props.menuMode === 'horizontal';
    const isSlim = props.menuMode === 'slim';
    const isHorizontalOrSlim = isHorizontal || isSlim;
    const isMobile = window.innerWidth < 992;
    const transitionTimeout = props.root ? 0 : { enter: 1000, exit: 450 };

    const onMenuItemClick = (
      event: React.SyntheticEvent,
      item: SideBarItem,
      index: number
    ) => {
      if (item.disabled) {
        event.preventDefault();
        return;
      }

      if (props.root && props.onRootItemClick) {
        props.onRootItemClick({
          originalEvent: event,
          item: item,
        });
      }

      //execute command
      if (item.command) {
        item.command({ originalEvent: event, item: item });
        event.preventDefault();
      }

      if (item.items) {
        setActiveIndex((prev) => (prev === index ? null : index));
      } else {
        setActiveIndex(index);

        if (props.menuMode !== 'static') {
          const ink = getInk(event.currentTarget);
          if (ink) {
            removeClass(ink, 'p-ink-active');
          }
        }
      }

      if (props.onMenuItemClick) {
        props.onMenuItemClick({
          originalEvent: event,
          item: item,
        });
      }
      return;
    };

    const visible = (item: SideBarItem) => {
      return typeof item.visible === 'function'
        ? item.visible()
        : item.visible !== false;
    };

    const isSubmenuOpen = (item: SideBarItem, active: boolean): boolean => {
      if (!item.items) return false;

      const collapseForMode =
        (isHorizontal || isSlim) &&
        !isMobile &&
        (!isSlim || activeIndex !== null);
      if (props.root && !collapseForMode) {
        return true;
      }

      return active;
    };

    useEffect(() => {
      const index =
        props.items?.findIndex(
          (item) => item.to && pathname.startsWith(item.to)
        ) ?? null;
      setActiveIndex(index);
    }, [pathname, props.items]);

    useEffect(() => {
      if (!props.menuActive && isHorizontalOrSlim && !isMobile) {
        setActiveIndex(null);
      }
    }, [props.menuActive, isHorizontalOrSlim, isMobile]);

    if (!props.items) {
      return null;
    }

    return (
      <>
        <ul ref={ref} className={props.className}>
          {props.items.map((item: SideBarItem, i: number) => {
            if (!visible(item)) {
              return null;
            }

            return (
              <AppMenuItem
                key={item.label ?? i}
                item={item}
                index={i}
                active={activeIndex === i}
                root={props.root}
                menuMode={props.menuMode}
                menuActive={props.menuActive}
                onMenuItemClick={onMenuItemClick}
                onSubmenuItemClick={props.onMenuItemClick}
                isSubmenuOpen={isSubmenuOpen}
                transitionTimeout={transitionTimeout}
                Submenu={AppSubmenu}
              />
            );
          })}
        </ul>
        {isSlim && props.root && (
          <Tooltip target="li:not(.active-menuitem)>.tooltip" />
        )}
      </>
    );
  }
);

const AppMenu = ({
  items,
  active,
  onRootMenuItemClick,
  onMenuItemClick,
  menuMode,
}: AppMenuProps) => {
  return (
    <AppSubmenu
      items={items}
      className="layout-menu"
      menuActive={active}
      onRootItemClick={onRootMenuItemClick}
      onMenuItemClick={onMenuItemClick}
      root={true}
      menuMode={menuMode}
      parentMenuItemActive={true}
    />
  );
};

export default AppMenu;
